import { useState, useEffect, useCallback } from 'react';
import { api, ApiError } from '../lib/api';
import { AuthImage } from '../components/AuthImage';
import {
  newsApi, NEWS_KINDS, NEWS_AUDIENCES, kindEmoji,
  type NewsPost, type AudiencePreview,
} from '../lib/newsApi';

/**
 * The staff surface for the School News & Achievements feed. Compose a post ("1st Rank — Aditya"),
 * attach photos, preview who it reaches, and publish — publishing notifies every parent in the
 * audience through the same outbox as announcements.
 */
export function NewsAdminPage() {
  const [tab, setTab] = useState<'posts' | 'compose'>('posts');
  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Communication</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>News & achievements</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: 640 }}>
          Ranks, results, sports wins, events — post them with photos and every parent in the audience
          is notified. Published posts appear in the family dashboard feed.
        </p>
      </div>
      <div className="tab-strip" style={{ gap: 4, marginBottom: 16 }}>
        <TabBtn active={tab === 'posts'} onClick={() => setTab('posts')}>Posts</TabBtn>
        <TabBtn active={tab === 'compose'} onClick={() => setTab('compose')}>Compose</TabBtn>
      </div>
      {tab === 'posts' ? <PostsTab /> : <ComposeTab onDone={() => setTab('posts')} />}
    </div>
  );
}

function PostsTab() {
  const [rows, setRows] = useState<NewsPost[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    newsApi.list({ take: 50 }).then((r) => setRows(r.items)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const publish = async (id: string) => {
    setBusyId(id);
    try { await newsApi.publish(id); load(); } catch { /* */ } finally { setBusyId(null); }
  };
  const archive = async (id: string) => {
    if (!window.confirm('Take this post down?')) return;
    setBusyId(id);
    try { await newsApi.archive(id); load(); } catch { /* */ } finally { setBusyId(null); }
  };

  const togglePublic = async (p: NewsPost) => {
    setBusyId(p.id);
    try { await newsApi.setPublic(p.id, !p.isPublic); load(); } catch { /* */ } finally { setBusyId(null); }
  };

  if (rows === null) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;
  if (rows.length === 0) return <p style={{ color: 'var(--ink-3)', fontSize: 14 }}>No posts yet. Use Compose to create your first.</p>;

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {rows.map((p) => (
        <div key={p.id} className="card" style={{ padding: 14, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div style={{ fontSize: 26 }}>{kindEmoji(p.kind)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              {p.kind}{p.highlight ? ` · ${p.highlight}` : ''}{p.studentName ? ` · ${p.studentName}` : ''}
            </div>
            <div style={{ fontWeight: 600 }}>{p.title}</div>
            {p.body && <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>{p.body.slice(0, 120)}{p.body.length > 120 ? '…' : ''}</div>}
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>
              {p.status === 'Published'
                ? `Published to ${p.recipientCount} · ${new Date(p.publishedAt!).toLocaleDateString('en-IN')}`
                : p.status}
              {p.media.length > 0 && ` · ${p.media.length} media`}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {p.status === 'Draft' && (
              <button className="btn btn-accent" disabled={busyId === p.id} onClick={() => publish(p.id)}>Publish</button>
            )}
            {p.status === 'Published' && (
              <button className="btn" disabled={busyId === p.id}
                title={p.isPublic ? 'Showing on your public page' : 'Show this on your public page'}
                onClick={() => togglePublic(p)}
                style={p.isPublic ? { borderColor: 'var(--brand)', color: 'var(--brand)', fontWeight: 600 } : undefined}>
                {p.isPublic ? '🌐 Public' : 'Make public'}
              </button>
            )}
            {p.status !== 'Archived' && (
              <button className="btn" disabled={busyId === p.id} onClick={() => archive(p.id)}>Archive</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ComposeTab({ onDone }: { onDone: () => void }) {
  const [kind, setKind] = useState('Achievement');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [highlight, setHighlight] = useState('');
  const [audience, setAudience] = useState('AllFamilies');
  const [pinned, setPinned] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [sendSms, setSendSms] = useState(false);
  const [institutionId, setInstitutionId] = useState<string>('');
  const [draft, setDraft] = useState<NewsPost | null>(null);
  const [preview, setPreview] = useState<AudiencePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // resolve the institution once (news posts belong to an institution)
  useEffect(() => {
    api.get<{ id: string }[]>('/api/institutions').then((xs) => { if (xs[0]) setInstitutionId(xs[0].id); }).catch(() => {});
  }, []);

  const saveDraft = async () => {
    if (!institutionId || !title.trim()) { setError('A title is required.'); return; }
    setBusy(true); setError(null);
    try {
      const p = await newsApi.create({
        institutionId, kind, title, body, highlight: highlight || null,
        audience, audienceIds: [], notifyOnPublish: true, sendEmail, sendSms,
        pinned, expiresOn: null,
      });
      setDraft(p);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save the draft.');
    } finally { setBusy(false); }
  };

  const addMedia = async (file: File) => {
    if (!draft) return;
    setBusy(true);
    try { setDraft(await newsApi.uploadMedia(draft.id, file)); } catch { setError('Could not upload.'); } finally { setBusy(false); }
  };

  const doPreview = async () => {
    if (!institutionId) return;
    try { setPreview(await newsApi.preview(institutionId, audience, [])); } catch { /* */ }
  };

  const publish = async () => {
    if (!draft) return;
    setBusy(true); setError(null);
    try {
      await newsApi.publish(draft.id);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not publish.');
    } finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ padding: 20, maxWidth: 640 }}>
      {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', justifyContent: 'flex-start', marginBottom: 12 }}>{error}</div>}

      <label className="label">Type</label>
      <select className="input" value={kind} onChange={(e) => setKind(e.target.value)} style={{ marginBottom: 12 }} disabled={!!draft}>
        {NEWS_KINDS.map((k) => <option key={k} value={k}>{kindEmoji(k)} {k}</option>)}
      </select>

      <label className="label">Highlight (badge — e.g. "1st Rank", "District Gold")</label>
      <input className="input" value={highlight} onChange={(e) => setHighlight(e.target.value)} placeholder="Optional" style={{ marginBottom: 12 }} disabled={!!draft} />

      <label className="label">Title</label>
      <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Aditya Rao tops Class 10 CBSE" style={{ marginBottom: 12 }} disabled={!!draft} />

      <label className="label">Details</label>
      <textarea className="input" value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="A few lines about the achievement…" style={{ marginBottom: 12, resize: 'vertical' }} disabled={!!draft} />

      <label className="label">Audience</label>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <select className="input" value={audience} onChange={(e) => { setAudience(e.target.value); setPreview(null); }} disabled={!!draft}>
          {NEWS_AUDIENCES.map((a) => <option key={a} value={a}>{audienceLabel(a)}</option>)}
        </select>
        <button className="btn" onClick={doPreview} disabled={!!draft}>Who?</button>
      </div>
      {preview && <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 12 }}>Reaches <strong>{preview.count}</strong>{preview.sampleNames.length > 0 ? ` — e.g. ${preview.sampleNames.join(', ')}` : ''}</div>}

      <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, cursor: 'pointer' }}><input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} disabled={!!draft} /> Pin to top</label>
        <label style={{ fontSize: 13, cursor: 'pointer' }}><input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} disabled={!!draft} /> Also email</label>
        <label style={{ fontSize: 13, cursor: 'pointer' }}><input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} disabled={!!draft} /> Also SMS</label>
      </div>

      {!draft ? (
        <button className="btn btn-accent" style={{ width: '100%', justifyContent: 'center' }} disabled={busy || !title.trim()} onClick={saveDraft}>
          {busy ? 'Saving…' : 'Save draft & add photos'}
        </button>
      ) : (
        <>
          <div style={{ borderTop: '1px solid var(--line)', paddingTop: 16, marginBottom: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Photos & files</div>
            {draft.media.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                {draft.media.map((m) => (
                  m.kind === 'Image'
                    ? <AuthImage key={m.id} url={m.url} style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--line)' }} />
                    : <div key={m.id} style={{ width: 72, height: 72, display: 'grid', placeItems: 'center', borderRadius: 8, border: '1px solid var(--line)', fontSize: 11, color: 'var(--ink-3)' }}>{m.kind}</div>
                ))}
              </div>
            )}
            <input type="file" accept="image/*,video/*,application/pdf" disabled={busy}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) addMedia(f); }} />
          </div>
          <button className="btn btn-accent" style={{ width: '100%', justifyContent: 'center' }} disabled={busy} onClick={publish}>
            {busy ? 'Publishing…' : 'Publish & notify'}
          </button>
        </>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer',
      fontWeight: active ? 600 : 400, color: active ? 'var(--ink)' : 'var(--ink-3)',
      borderBottom: active ? '2px solid var(--brand)' : '2px solid transparent', marginBottom: -1,
    }}>{children}</button>
  );
}

function audienceLabel(a: string): string {
  switch (a) {
    case 'AllFamilies': return 'All families';
    case 'Everyone': return 'Everyone';
    case 'AllStaff': return 'All staff';
    case 'ClassFamilies': return 'Specific classes';
    case 'RouteFamilies': return 'Specific bus routes';
    default: return a;
  }
}
